import React, { useRef, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import ChartHeader from "./ChartHeader"; // Local Header
import ChartContainer from "./ChartContainer"; // Local Container
import MDBox from "components/MDBox";
import MetricCardsContainer from "./MetricCardsContainer"; // Local Metric Cards
import MDTypography from "components/MDTypography";
import ChartAssistant from "components/ChartAssistant";

// MUI platform icons (reusing helper from VerticalBarChart or duplicating if not exported)
import FacebookRoundedIcon from "@mui/icons-material/FacebookRounded";
import YouTubeIcon from "@mui/icons-material/YouTube";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/Twitter";

// HorizontalBarChart configurations
import configs from "examples/Charts/BarCharts/HorizontalBarChart/configs";
import colors from "assets/theme/base/colors";

// Chart.js components
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from "chart.js";
import ChartNotEntitled from "examples/Charts/General/ChartNotEntitled";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

// Helper for Icons
const getPlatformIcon = (platform) => {
  switch (platform?.toLowerCase()) {
    case "facebook": return <FacebookRoundedIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />;
    case "youtube": return <YouTubeIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />;
    case "instagram": return <InstagramIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />;
    case "twitter": return <TwitterIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />;
    default: return null;
  }
};

// 🚀 PLUGIN: Full-Width Horizontal Highlight (Light Grey)
const renderHorizontalHighlight = {
  id: "renderHorizontalHighlight",
  afterDraw: (chart) => {
    if (!chart.tooltip || !chart.tooltip._active || chart.tooltip._active.length === 0) return;
    const ctx = chart.ctx;
    const activePoint = chart.tooltip._active[0];
    if (!activePoint?.element) return;

    const y = activePoint.element.y; // Center of the bar (horizontal)
    const chartArea = chart.chartArea;
    const scaleY = chart.scales.y;
    if (!scaleY) return;

    // Calculate Grid Row Height
    const tick0 = scaleY.getPixelForTick(0);
    const tick1 = scaleY.getPixelForTick(1);
    const height = tick1 && tick0 ? Math.abs(tick1 - tick0) : 0;

    if (y < chartArea.top || y > chartArea.bottom) return;

    ctx.save();
    ctx.fillStyle = "rgba(0, 0, 0, 0.05)"; // Light Grey Highlight
    // Draw full width, centered on the Y position
    ctx.fillRect(chartArea.left, y - height / 2, chartArea.right - chartArea.left, height);
    ctx.restore();
  },
};

// 🚀 PLUGIN: Left-to-Right Reveal Animation
const leftToRightRevealPlugin = {
  id: "leftToRightReveal",
  beforeInit(chart, args, options) {
    chart.$revealAnimation = {
      startTime: null,
      duration: options.duration || 2000,
      running: true,
    };
    chart.resetRevealAnimation = () => {
      chart.$revealAnimation.startTime = null;
      chart.$revealAnimation.running = true;
      // Ensure chart is still attached before updating
      if (chart.ctx) {
        chart.update();
      }
    };
  },
  beforeDatasetsDraw(chart) {
    const chartArea = chart.chartArea;
    const ctx = chart.ctx;
    if (!chartArea || !ctx) return;
    const animationState = chart.$revealAnimation;
    if (!animationState.startTime) {
      animationState.startTime = performance.now();
    }
    const now = performance.now();
    const elapsed = now - animationState.startTime;
    const progress = Math.min(elapsed / animationState.duration, 1);

    // For Horizontal Charts, we might want top-to-bottom or left-to-right?
    // VerticalBarChart does left-to-right.
    // Horizontal bars grow from left to right normally.
    // So "Left-to-Right" masking works perfectly here too (masking the bars as they extend).

    const clipWidth = chartArea.left + (chartArea.right - chartArea.left) * progress;
    ctx.save();
    ctx.beginPath();
    ctx.rect(chartArea.left, chartArea.top, clipWidth - chartArea.left, chartArea.bottom - chartArea.top);
    ctx.clip();
    if (progress < 1 && animationState.running !== false) {
      animationState.running = true;
      requestAnimationFrame(() => {
        // 🛡️ Safety Warning: Ensure chart is actively attached before updating to prevent "ownerDocument" errors
        if (chart.ctx && chart.canvas && chart.canvas.isConnected) {
          chart.update();
        }
      });
    } else {
      animationState.running = false;
    }
  },
  afterDatasetsDraw(chart) {
    if (chart.ctx) {
      chart.ctx.restore();
    }
  },
};

function HorizontalBarChart({
  title = "",
  description = "",
  height = "19.125rem",
  chart,
  metrics,
  is_derived = false,
  isEntitled = true,
  featureName = "",
  planRequired = "",
  rawData = null,
  entitlements,
  stacked = false, // ✅ ADDED: Stacked Prop
}) {
  const chartContainerRef = useRef(null);

  // 1. Prepare Chart Data & Options
  const { data, options } = configs(chart.labels || [], chart.datasets || [], stacked);

  useEffect(() => {
    if (chartContainerRef.current?.resetRevealAnimation) {
      chartContainerRef.current.resetRevealAnimation();
    }
  }, [chart]); // Re-run animation when data changes

  // 2. Prepare Metric Cards Data
  const validDatasets = chart.datasets?.filter(Boolean) || [];

  // ✅ FIX: Prioritize passed 'metrics' prop if available (handles "No Data" states where chart.datasets might be empty)
  let profileMetrics = [];

  if (metrics && metrics.length > 0) {
    profileMetrics = metrics;
  } else {
    // Fallback: Derive from datasets
    profileMetrics = validDatasets.map((dataset, index) => {
      let currentMetricValue = dataset.data?.reduce((a, b) => a + Math.abs(b), 0) || 0; // Absolute sum

      return {
        profilePic: dataset.profilePictureUrl || "https://via.placeholder.com/150",
        name: dataset.label || "Unknown Profile",
        platform: dataset.platform || "Unknown",
        metricName: dataset.metricName || "Total",
        metricValue: currentMetricValue.toLocaleString(),
        previousMetricValue: null,
        isQualitative: false,
      };
    });
  }

  const hasData = data?.datasets?.some(ds => ds.data.some(val => val !== 0));

  // Inject Plugin Options
  const finalOptions = {
    ...options,
    plugins: {
      ...options.plugins,
      leftToRightReveal: { duration: 1500 },
    }
  };

  const allPlugins = [renderHorizontalHighlight, leftToRightRevealPlugin];

  return (
    <Card>
      <ChartHeader
        title={title}
        description={description}
        profilePictures={profileMetrics.map((metric) => metric.profilePic)}
        is_derived={is_derived}
      >
        <ChartAssistant
          chartData={chart}
          title={title}
          context={description}
          rawData={rawData}
          sx={{ position: "static", ml: 2, boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)" }}
          entitlements={entitlements}
        />
      </ChartHeader>

      <div style={{ position: "relative" }}>
        {!isEntitled && (
          <ChartNotEntitled featureName={featureName} planRequired={planRequired} />
        )}

        <MetricCardsContainer metrics={profileMetrics} />

        {/* Legend */}
        {hasData && (
          <MDBox display="flex" justifyContent="center" flexWrap="wrap" px={2} py={1}>
            {data.datasets.map((ds, i) => (
              <MDBox key={i} display="flex" alignItems="center" mr={2} mb={1}>
                {/* Use solid color for legend box */}
                <MDBox
                  sx={{
                    width: 20,
                    height: 10,
                    borderRadius: "2px",
                    backgroundColor: ds.backgroundColor, // Simplified color handling
                    mr: 1,
                  }}
                />
                {getPlatformIcon(validDatasets[i]?.platform)}
                <MDTypography variant="button" color="text" sx={{ fontWeight: 300, ml: 0.5 }}>
                  {ds.label}
                </MDTypography>
              </MDBox>
            ))}
          </MDBox>
        )}

        <MDBox py={2} pr={2} pl={0}>
          {hasData ? (
            <ChartContainer
              ref={chartContainerRef}
              type="bar" // 'bar' with indexAxis: 'y' in options creates horizontal
              height={height}
              data={data}
              options={finalOptions} // ✅ Use Final Options
              plugins={allPlugins} // ✅ Pass Plugins
            />
          ) : (
            <MDBox sx={{ p: "10px", height: "10rem", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <MDTypography variant="subtitle1" color="text" align="center">
                No data available for the selected date range.
              </MDTypography>
            </MDBox>
          )}
        </MDBox>
      </div>

      {is_derived && (
        <MDBox p={1} display="flex" justifyContent="flex-end">
          <MDTypography variant="caption" color="text" sx={{ fontStyle: "italic", fontSize: "0.7rem", opacity: 0.7, mr: 1 }}>
            *Metric calculated by Comparedly
          </MDTypography>
        </MDBox>
      )}
    </Card>
  );
}

HorizontalBarChart.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  chart: PropTypes.objectOf(PropTypes.array).isRequired,
  metrics: PropTypes.array,
  is_derived: PropTypes.bool,
  isEntitled: PropTypes.bool,
  featureName: PropTypes.string,
  planRequired: PropTypes.string,
  rawData: PropTypes.any,
  entitlements: PropTypes.object,
};

export default HorizontalBarChart;
